import { Cpu, Shield, Zap, ChevronDown, ChevronRight, PanelRightClose } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { timeAgo } from '@/lib/utils'
import { useAppStore } from '@/store'
import { useHealth, useApprovals, useMcpStats, useTokenUsage } from '@/hooks/useApi'
import { useState } from 'react'
import type { ActivityEvent } from '@/types'

const SESSION_CONTEXT = {
  user: 'dervent_wright',
  role: 'super_admin',
  environment: 'production',
  session: 'active',
  connected: 'ops-bridge MCP',
  tierMode: 'auto-resolve',
}

function ActivityItem({ event }: { event: ActivityEvent }) {
  const severityColors = {
    success: 'bg-success',
    warning: 'bg-warning',
    error: 'bg-danger',
    info: 'bg-info',
  }

  return (
    <div className="flex gap-2.5 py-2 group">
      <div className={cn('h-2 w-2 rounded-full mt-1.5 shrink-0', severityColors[event.severity ?? 'info'])} />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-text-primary truncate">{event.title}</p>
        <p className="text-[11px] text-text-muted truncate">{event.description}</p>
        <span className="text-[10px] text-text-muted">{timeAgo(event.timestamp)}</span>
      </div>
    </div>
  )
}

export function ContextPanel() {
  const { contextPanelOpen, toggleContextPanel } = useAppStore()
  const activityFeed = useAppStore((s) => s.activityFeed)
  const [sessionExpanded, setSessionExpanded] = useState(true)

  const { data: health } = useHealth()
  const { data: approvals } = useApprovals()
  const { data: mcpStats } = useMcpStats()
  const { data: tokenUsage } = useTokenUsage()

  // When closed — show a slim tab to reopen
  if (!contextPanelOpen) {
    return (
      <div className="flex flex-col items-center border-r border-border bg-surface/30 w-8 shrink-0">
        <button
          onClick={toggleContextPanel}
          className="flex flex-col items-center gap-1 py-3 px-1 text-text-muted hover:text-text-primary transition-colors group w-full"
          title="Open context panel"
        >
          <ChevronRight className="h-3.5 w-3.5 group-hover:text-accent" />
          <span className="text-[9px] tracking-widest rotate-90 mt-2 uppercase font-medium whitespace-nowrap opacity-60">Context</span>
        </button>
      </div>
    )
  }

  return (
    <aside className="w-72 border-r border-border bg-surface/50 backdrop-blur-sm flex flex-col h-full shrink-0">
      <div className="px-3 h-10 flex items-center justify-between border-b border-border">
        <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Context</span>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={toggleContextPanel}
          title="Collapse context panel"
        >
          <PanelRightClose className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <div className="p-3 space-y-4">
          <div>
            <button
              onClick={() => setSessionExpanded(!sessionExpanded)}
              className="flex items-center justify-between w-full text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2 cursor-pointer"
            >
              <div className="flex items-center gap-1.5">
                <Cpu className="h-3 w-3" />
                Session
              </div>
              <ChevronDown className={cn('h-3 w-3 transition-transform', !sessionExpanded && '-rotate-90')} />
            </button>
            {sessionExpanded && (
              <div className="space-y-1.5 text-xs">
                {Object.entries(SESSION_CONTEXT).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-text-muted capitalize">{key}</span>
                    <span className="text-text-primary font-medium">{String(value)}</span>
                  </div>
                ))}
                <div className="flex justify-between">
                  <span className="text-text-muted">tools</span>
                  <span className="text-text-primary font-medium">{mcpStats?.tool_count ?? '—'}</span>
                </div>
              </div>
            )}
          </div>

          <div className="h-px bg-border" />

          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Shield className="h-3 w-3 text-text-secondary" />
              <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Quick Stats</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="glass-card rounded-lg p-2 text-center">
                <div className="text-lg font-bold text-success">{health ? `${health.platform_health}%` : '—'}</div>
                <div className="text-[10px] text-text-muted">Uptime</div>
              </div>
              <div className="glass-card rounded-lg p-2 text-center">
                <div className="text-lg font-bold text-accent">{mcpStats?.tool_count ?? '—'}</div>
                <div className="text-[10px] text-text-muted">Tools</div>
              </div>
              <div className="glass-card rounded-lg p-2 text-center">
                <div className="text-lg font-bold text-warning">{approvals?.count ?? '—'}</div>
                <div className="text-[10px] text-text-muted">Pending</div>
              </div>
              <div className="glass-card rounded-lg p-2 text-center">
                <div className="text-lg font-bold text-info">{health?.total_workers ?? '—'}</div>
                <div className="text-[10px] text-text-muted">Workers</div>
              </div>
            </div>
            {tokenUsage && (
              <div className="grid grid-cols-3 gap-2 mt-2">
                <div className="glass-card rounded-lg p-2 text-center">
                  <div className="text-sm font-bold text-success">{tokenUsage.memory_hit_rate}%</div>
                  <div className="text-[9px] text-text-muted">Memory</div>
                </div>
                <div className="glass-card rounded-lg p-2 text-center">
                  <div className="text-sm font-bold text-accent">{tokenUsage.cache_hit_rate}%</div>
                  <div className="text-[9px] text-text-muted">Cache</div>
                </div>
                <div className="glass-card rounded-lg p-2 text-center">
                  <div className="text-sm font-bold text-info">{tokenUsage.tokens_today > 1000 ? `${(tokenUsage.tokens_today / 1000).toFixed(1)}k` : tokenUsage.tokens_today}</div>
                  <div className="text-[9px] text-text-muted">Tokens</div>
                </div>
              </div>
            )}
          </div>

          <div className="h-px bg-border" />

          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Zap className="h-3 w-3 text-text-secondary" />
              <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Activity Feed</span>
            </div>
            <div className="space-y-0.5">
              {activityFeed.length > 0 ? (
                activityFeed.slice(0, 20).map((event) => (
                  <ActivityItem key={event.id} event={event} />
                ))
              ) : (
                <div className="text-center py-4 text-[11px] text-text-muted">
                  Live activity will appear here
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </aside>
  )
}
